import type { PdfPageSearchQuery } from '@affine/graphql';
import { pdfPageSearchQuery } from '@affine/graphql';

import type {
  PDFFindCoverage,
  PDFFindNotice,
  PDFFindSourceMode,
  PDFPageSearchHit,
} from './pdf-find-controller';
import {
  type LocalPdfFindProvider,
  runLocalPdfFind,
} from './pdf-local-find-provider';
import { mapPdfSearchHits } from './pdf-search-hit-mapping';

export interface PdfFindServer {
  gql(input: {
    query: typeof pdfPageSearchQuery;
    variables: {
      id: string;
      input: {
        attachmentBlockId: string;
        keyword: string;
        limit: number;
      };
    };
  }): Promise<PdfPageSearchQuery>;
}

export type RemotePdfFindResult =
  | {
      error: 'offline' | 'search-failed';
      kind: 'error';
    }
  | {
      hits: PDFPageSearchHit[];
      kind: 'results';
    };

export interface RunRemotePdfFindQueryInput {
  attachmentBlockId: string;
  limit: number;
  query: string;
  server: PdfFindServer | null | undefined;
  workspaceId: string;
}

export interface PdfFindGateInput {
  modelPresent: boolean;
  open: boolean;
  query: string;
  supportsPdfSearchIndex: boolean;
}

export interface HybridPdfFindInput {
  attachmentBlockId: string;
  docId: string;
  limit: number;
  localProvider: LocalPdfFindProvider;
  pageCount: number;
  query: string;
  server: PdfFindServer | null | undefined;
  signal?: AbortSignal;
  startPageIndex?: number;
  supportsPdfSearchIndex: boolean;
  workspaceId: string;
}

export interface PdfFindSourceUpdate {
  coverage?: PDFFindCoverage | null;
  hits: PDFPageSearchHit[];
  notice: PDFFindNotice;
  sourceMode: PDFFindSourceMode;
}

export function shouldRunRemotePdfFind({
  modelPresent,
  open,
  query,
  supportsPdfSearchIndex,
}: PdfFindGateInput) {
  return (
    open && modelPresent && supportsPdfSearchIndex && query.trim().length > 0
  );
}

export async function runRemotePdfFindQuery({
  attachmentBlockId,
  limit,
  query,
  server,
  workspaceId,
}: RunRemotePdfFindQueryInput): Promise<RemotePdfFindResult> {
  if (!server) return { kind: 'error', error: 'offline' };

  try {
    const result = await server.gql({
      query: pdfPageSearchQuery,
      variables: {
        id: workspaceId,
        input: {
          attachmentBlockId,
          keyword: query,
          limit,
        },
      },
    });
    return {
      kind: 'results',
      hits: mapPdfSearchHits(result, attachmentBlockId),
    };
  } catch {
    return { kind: 'error', error: 'search-failed' };
  }
}

export async function runHybridPdfFindSource(
  input: HybridPdfFindInput,
  emit: (update: PdfFindSourceUpdate) => void
) {
  let localHits: PDFPageSearchHit[] = [];
  let remoteHits: PDFPageSearchHit[] = [];
  let coverage: PDFFindCoverage | null = null;
  let remoteUnavailable = false;

  const emitMerged = () => {
    emit({
      coverage,
      hits: mergePdfFindHits(localHits, remoteHits),
      notice: createFindNotice({
        coverage,
        hasServer: Boolean(input.server && input.supportsPdfSearchIndex),
        localHits,
        remoteUnavailable,
      }),
      sourceMode: createSourceMode({
        hasRemote: remoteHits.length > 0,
        hasServer: Boolean(input.server && input.supportsPdfSearchIndex),
      }),
    });
  };

  const remotePromise =
    input.server && input.supportsPdfSearchIndex
      ? runRemotePdfFindQuery({
          attachmentBlockId: input.attachmentBlockId,
          limit: input.limit,
          query: input.query,
          server: input.server,
          workspaceId: input.workspaceId,
        }).then(result => {
          if (input.signal?.aborted) return;
          if (result.kind === 'results') {
            remoteHits = result.hits;
          } else {
            remoteUnavailable = true;
          }
          if (!coverage && remoteHits.length === 0) return;
          emitMerged();
        })
      : Promise.resolve();

  try {
    for await (const update of runLocalPdfFind({
      attachmentBlockId: input.attachmentBlockId,
      docId: input.docId,
      pageCount: input.pageCount,
      provider: input.localProvider,
      query: input.query,
      signal: input.signal,
      startPageIndex: input.startPageIndex,
    })) {
      if (input.signal?.aborted) return;
      coverage = update.coverage;
      localHits = update.hits;
      emitMerged();
    }
  } catch (error) {
    if (isAbortError(error)) return;
    coverage = createPdfErrorCoverage(input.pageCount, coverage);
    emitMerged();
  }

  await remotePromise;
}

export function mergePdfFindHits(
  localHits: PDFPageSearchHit[],
  remoteHits: PDFPageSearchHit[]
) {
  const merged = [...localHits];
  for (const remoteHit of remoteHits) {
    if (
      remoteHit.source !== 'ocr' &&
      hasEquivalentLocalHit(localHits, remoteHit)
    ) {
      continue;
    }
    merged.push(remoteHit);
  }
  return merged.sort((a, b) => a.pageIndex - b.pageIndex || b.score - a.score);
}

function hasEquivalentLocalHit(
  localHits: PDFPageSearchHit[],
  remoteHit: PDFPageSearchHit
) {
  return localHits.some(localHit => {
    if (localHit.pageIndex !== remoteHit.pageIndex) return false;
    if (hasRectOverlap(localHit, remoteHit)) return true;
    return (
      normalizeHitText(localHit.snippet) === normalizeHitText(remoteHit.snippet)
    );
  });
}

function hasRectOverlap(a: PDFPageSearchHit, b: PDFPageSearchHit) {
  return a.rects.some(aRect =>
    b.rects.some(bRect => {
      const left = Math.max(aRect.x, bRect.x);
      const top = Math.max(aRect.y, bRect.y);
      const right = Math.min(aRect.x + aRect.width, bRect.x + bRect.width);
      const bottom = Math.min(aRect.y + aRect.height, bRect.y + bRect.height);
      const intersection =
        Math.max(0, right - left) * Math.max(0, bottom - top);
      if (intersection <= 0) return false;
      const aArea = aRect.width * aRect.height;
      const bArea = bRect.width * bRect.height;
      return intersection / Math.max(0.000001, Math.min(aArea, bArea)) > 0.6;
    })
  );
}

function createFindNotice({
  coverage,
  hasServer,
  localHits,
  remoteUnavailable,
}: {
  coverage: PDFFindCoverage | null;
  hasServer: boolean;
  localHits: PDFPageSearchHit[];
  remoteUnavailable: boolean;
}): PDFFindNotice {
  if (coverage?.capReached) return 'cap-reached';
  if (coverage && !coverage.complete) return 'partial';
  if (remoteUnavailable && coverage) return 'remote-unavailable';
  if (!hasServer && coverage) {
    if (
      coverage.complete &&
      localHits.length === 0 &&
      coverage.textLayerPageIndexes.length === 0 &&
      coverage.emptyTextLayerPageIndexes.length > 0
    ) {
      return 'ocr-unavailable-offline';
    }
    return 'local-only';
  }
  return null;
}

function createSourceMode({
  hasRemote,
  hasServer,
}: {
  hasRemote: boolean;
  hasServer: boolean;
}): PDFFindSourceMode {
  if (hasRemote) return 'hybrid';
  return hasServer ? 'local' : 'local-only';
}

function createPdfErrorCoverage(
  pageCount: number,
  previous: PDFFindCoverage | null
): PDFFindCoverage {
  return {
    capReached: false,
    complete: false,
    emptyTextLayerPageIndexes: previous?.emptyTextLayerPageIndexes ?? [],
    incompleteReason: 'pdf-error',
    scanning: false,
    searchedPageIndexes: previous?.searchedPageIndexes ?? [],
    textLayerPageIndexes: previous?.textLayerPageIndexes ?? [],
    totalPageCount: pageCount,
  };
}

function normalizeHitText(value: string) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase()
    .replace(/\s+/g, '');
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}
